import * as React from 'react'
import { AlertTriangle, Check, Copy, Plus } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toolsService } from '@/services/toolsService'
import type { WorkspaceSecret } from '@/types/tool'

const CREATE_NEW_OPTION = '__create_new_secret__'

type SecretSelectFieldProps = {
  value: string
  onChange: (secretId: string) => void
  disabled?: boolean
}

export function SecretSelectField({ value, onChange, disabled }: SecretSelectFieldProps) {
  const [secrets, setSecrets] = React.useState<WorkspaceSecret[]>([])
  const [loadingSecrets, setLoadingSecrets] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [openCreate, setOpenCreate] = React.useState(false)
  const [createStep, setCreateStep] = React.useState<'form' | 'reveal'>('form')
  const [name, setName] = React.useState('')
  const [secretValue, setSecretValue] = React.useState('')
  const [revealedSecretValue, setRevealedSecretValue] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [copied, setCopied] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const resetCreateDialog = React.useCallback(() => {
    setCreateStep('form')
    setName('')
    setSecretValue('')
    setRevealedSecretValue('')
    setDescription('')
    setCopied(false)
    setError(null)
  }, [])

  const loadSecrets = React.useCallback(async () => {
    setLoadingSecrets(true)
    try {
      const items = await toolsService.listSecrets()
      setSecrets(items)
      setError(null)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'No se pudieron cargar los secretos.')
    } finally {
      setLoadingSecrets(false)
    }
  }, [])

  React.useEffect(() => {
    void loadSecrets()
  }, [loadSecrets])

  const selectedValue = React.useMemo(() => {
    if (!value) return undefined
    return value
  }, [value])

  const hasCurrentSecretInList = React.useMemo(() => {
    if (!value) return false
    return secrets.some((secret) => secret.id === value)
  }, [secrets, value])

  const handleCreateSecret = async () => {
    setCreating(true)
    try {
      const created = await toolsService.createSecret({
        name: name.trim(),
        description: description.trim() || undefined,
        secret_value: secretValue,
        is_enabled: true,
      })
      await loadSecrets()
      onChange(created.id)
      setRevealedSecretValue(secretValue)
      setCreateStep('reveal')
      setSecretValue('')
      setError(null)
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'No se pudo crear el secreto.')
    } finally {
      setCreating(false)
    }
  }

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(revealedSecretValue)
      setCopied(true)
    } catch {
      setError('No se pudo copiar al portapapeles.')
    }
  }

  return (
    <>
      <div className="space-y-2">
        <Select
          value={selectedValue}
          onValueChange={(nextValue) => {
            if (nextValue === CREATE_NEW_OPTION) {
              resetCreateDialog()
              setOpenCreate(true)
              return
            }
            onChange(nextValue)
          }}
          disabled={disabled || loadingSecrets}
        >
          <SelectTrigger>
            <SelectValue placeholder={loadingSecrets ? 'Cargando secretos...' : 'Seleccionar secreto'} />
          </SelectTrigger>
          <SelectContent>
            {value && !hasCurrentSecretInList && (
              <SelectItem value={value}>
                Secreto actual ({value.slice(0, 8)}...)
              </SelectItem>
            )}
            {secrets.length === 0 ? (
              <SelectItem value="__empty__" disabled>
                No hay secretos
              </SelectItem>
            ) : (
              secrets.map((secret) => (
                <SelectItem key={secret.id} value={secret.id}>
                  {secret.name}
                </SelectItem>
              ))
            )}
            <SelectItem value={CREATE_NEW_OPTION}>+ Crear nuevo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Dialog
        open={openCreate}
        onOpenChange={(nextOpen) => {
          setOpenCreate(nextOpen)
          if (!nextOpen) resetCreateDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createStep === 'form' ? 'Crear secreto' : 'Secreto creado'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {createStep === 'form' ? (
              <>
                <Alert className="border-warning/30 bg-warning/10">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertTitle>Atencion</AlertTitle>
                  <AlertDescription>
                    El valor del secreto se mostrara solo una vez despues de crearlo.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label htmlFor="secret-name">Nombre *</Label>
                  <Input id="secret-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="OPENAI_API_KEY" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secret-value">Valor *</Label>
                  <Input id="secret-value" value={secretValue} onChange={(event) => setSecretValue(event.target.value)} placeholder="sk-..." />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secret-description">Descripción</Label>
                  <Textarea id="secret-description" value={description} onChange={(event) => setDescription(event.target.value)} rows={2} placeholder="Opcional" />
                </div>
              </>
            ) : (
              <>
                <Alert className="border-warning/30 bg-warning/10">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertTitle>Copia y guardalo en un lugar seguro</AlertTitle>
                  <AlertDescription>
                    Una vez cierres este cuadro no volvera a mostrarse el valor del secreto.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>Valor del secreto</Label>
                  <Textarea value={revealedSecretValue} readOnly rows={3} className="font-mono" />
                </div>
              </>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpenCreate(false)}>
              Cancelar
            </Button>
            {createStep === 'form' ? (
              <Button type="button" onClick={handleCreateSecret} disabled={creating || loadingSecrets}>
                <Plus className="mr-1 h-4 w-4" />
                {creating ? 'Creando...' : 'Crear nuevo'}
              </Button>
            ) : (
              <Button type="button" onClick={handleCopySecret}>
                {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
                {copied ? 'Copiado' : 'Copiar valor'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
